import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import ResponsiveApiImg from "../ResponsiveApiImg";
import {
  SearchInitialStateWrapper,
  SectionWrapper,
  Title,
  ForYouCard,
  CardTitle,
  ForYouCardContent,
  FavBtn,
  ForYouCardShadow,
  Tag,
  HorizontalSwiper,
  CardsSwiper,
} from "./styles";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation } from "swiper";
import "swiper/swiper.scss";
import { getSafe } from "../../utils";
import { useMemo } from "react";
import { toggleFavorites } from "../../actions/favorites";
import FavHeartIcon from "../../icons/FavHeartIcon";
import NewFavIcon from "../../icons/NewFavIcon";
import { useFetchPlaces } from "../../hooks/useFetchPlaces";

// Props: recentSearches (array of strings), onSearchChip(term), onSearchChipClear(term), onSelectPlace(place)
const SearchInitialState = ({
  recentSearches = [],
  onSearchChip,
  onSearchChipClear,
  onSelectPlace,
}) => {
  const { t } = useTranslation();
  const { data: placesResult } = useFetchPlaces();
  const places = useMemo(
    () => placesResult?.places ?? placesResult?.all ?? [],
    [placesResult]
  );
  const { local } = useSelector((state) => state.Local);
  const { favorites } = useSelector((state) => state.Favorites || {});
  const dispatch = useDispatch();

  // Pick random 5 places for now
  const randomPlaces = useMemo(() => {
    return (places || [])
      .slice()
      .sort(() => 0.5 - Math.random())
      .slice(0, 5);
  }, [places]);

  SwiperCore.use([Navigation]);
  return (
    <SearchInitialStateWrapper>
      <SectionWrapper>
        <Title>{t("recentSearch")}</Title>
        {recentSearches?.length === 0 && (
          <span style={{ fontSize: 14, color: "#444", fontFamily: "Ivy" }}>
            {t("noRecentSearches")}
          </span>
        )}
        {Array.isArray(recentSearches) && recentSearches.length > 0 && (
          <HorizontalSwiper>
            <Swiper
              slidesPerView={"auto"}
              spaceBetween={8}
              freeMode
              allowTouchMove
            >
              {recentSearches.map((term) => (
                <SwiperSlide key={term} style={{ width: "auto" }}>
                  <Tag>
                    <p onClick={() => onSearchChip?.(term)}>{term}</p>
                    <span
                      role="button"
                      aria-label={t(
                        "removeRecentSearch",
                        "Remove recent search"
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSearchChipClear?.(term);
                      }}
                    >
                      X
                    </span>
                  </Tag>
                </SwiperSlide>
              ))}
            </Swiper>
          </HorizontalSwiper>
        )}
      </SectionWrapper>

      <SectionWrapper>
        <Title>{t("forYou")}</Title>
        <CardsSwiper>
          <Swiper
            slidesPerView={"auto"}
            spaceBetween={16}
            allowTouchMove
            freeMode
          >
            {randomPlaces.map((p) => {
              const isFav = !!favorites?.pois?.includes(p._id);
              return (
                <SwiperSlide key={p._id}>
                  <ForYouCard>
                    <ForYouCardContent
                      to={`/${local}/poi/${p._id}`}
                      onClick={() => {
                        onSelectPlace?.(p);
                      }}
                    >
                      <FavBtn
                        aria-label={
                          isFav
                            ? t("removeFromFavorites", "Remove from favorites")
                            : t("addToFavorites", "Add to favorites")
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          dispatch(toggleFavorites({ pois: [p._id] }));
                        }}
                      >
                        {isFav ? (
                          <FavHeartIcon width={13} height={13} />
                        ) : (
                          <NewFavIcon width={13} height={13} />
                        )}
                      </FavBtn>
                      <CardTitle>{getSafe(() => p.title[local])}</CardTitle>
                    </ForYouCardContent>
                    <ForYouCardShadow />
                    <ResponsiveApiImg
                      src={getSafe(() => p.media[0].url)}
                      alt={getSafe(() => p.title[local], "")}
                      sizes="(max-width: 480px) 210px, 300px"
                      loading="lazy"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 1,
                      }}
                    />
                  </ForYouCard>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </CardsSwiper>
      </SectionWrapper>
    </SearchInitialStateWrapper>
  );
};

export default SearchInitialState;
